import { format, formatDistanceToNow } from 'date-fns';
import { CurrencyType } from 'enum/currency';

function formatDate(dateString: string): string {
  const months: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const date = new Date(dateString);
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  const formattedDate = `${day < 10 ? '0' + day : day} -${month}-${year}`;
  return formattedDate;
}

function convertUnixTimestamp(unixTimestamp: number): string {
  const date = new Date(unixTimestamp * 1000);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  return formattedDateTime;
}

const formatNumberToCurrency = (
  number: number,
  currencySymbol: boolean,
  currencyType: CurrencyType = CurrencyType.INR,
  shortForm = false
): string => {
  const absNumber = Math.abs(number);
  let formattedNumber: string;

  if (currencyType === CurrencyType.INR) {
    if (shortForm && absNumber >= 1_00_00_000) {
      formattedNumber = `${(number / 1_00_00_000).toFixed(2)}Cr`;
    } else if (shortForm && absNumber >= 1_00_000) {
      formattedNumber = `${(number / 1_00_000).toFixed(2)}L`;
    } else {
      const isNegative = number < 0;
      const [integerPart, decimalPart] = absNumber.toFixed(2).split('.');
      const lastThree = integerPart.slice(-3);
      const otherNumbers = integerPart.slice(0, -3);
      const formattedIntegerPart = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + (otherNumbers ? ',' : '') + lastThree;
      formattedNumber = `${isNegative ? '-' : ''}${formattedIntegerPart}.${decimalPart}`;
    }

    return currencySymbol ? `₹ ${formattedNumber}` : formattedNumber;
  } else if (currencyType === CurrencyType.USD) {
    if (shortForm && absNumber >= 1_000_000_000) {
      formattedNumber = `${(number / 1_000_000_000).toFixed(2)}B`;
    } else if (shortForm && absNumber >= 1_000_000) {
      formattedNumber = `${(number / 1_000_000).toFixed(2)}M`;
    } else {
      const [integerPart, decimalPart] = number.toFixed(2).split('.');
      const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      formattedNumber = `${formattedIntegerPart}.${decimalPart}`;
    }

    return currencySymbol ? `$ ${formattedNumber}` : formattedNumber;
  }

  return number.toString();
};

type SupportedFormats =
  | 'dd-MMM-yyyy'
  | 'MMM dd, yyyy'
  | 'MMM dd, yyyy HH:mm'
  | 'MMM dd, yyyy HH:mm:ss'
  | 'dd-MMM-yyyy HH:mm:ss a'
  | 'MMM dd, hh:mm'
  | 'yyyy'
  | 'MMMM, y'
  | 'dd-MM-yy HH:mm:ss'
  | 'dd-MMM-yyyy, hh:mm a'
  | "yyyy-MM-dd'T'HH:mm:ss"
  | 'HH:mm:ss'
  | 'EEE dd-MMM-yyyy, hh:mm a'
  | 'dd-MMM-yyyy hh:mm a'
  | 'ddMMMyy'
  | 'dd-MMM-yyyy, hh:mm:ss a'
  | 'yyyy-MM-dd';

function formatDateCommon(dateString: string | number, targetFormat: SupportedFormats): string {
  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      throw new Error('Invalid Date');
    }

    const formattedDate = format(date, targetFormat);

    return formattedDate;
  } catch (error) {
    return '';
  }
}
function formatRelativeTime(dateString: string | number | Date): string {
  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      throw new Error('Invalid Date');
    }

    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    return '';
  }
}

export { formatDate, convertUnixTimestamp, formatNumberToCurrency, formatDateCommon, formatRelativeTime };
